// Import statements remain the same
import {
  DataGrid,
  getDefaultGridFilterModel,
  gridClasses,
  GridFilterModel,
  GridPaginationModel,
  GridRow,
  GridRowId,
  GridRowProps,
  GridSortModel
} from '@mui/x-data-grid';

// Styles import
import { alpha, styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import {
  setRowsPerPage,
  setPageNumber,
  setSortModel,
  setFilterModel,
  resetFilterModel,
  clearSortModel
} from 'store/pagination/paginationSlice';
import { useState, memo, useCallback, useEffect, useRef } from 'react';

const ODD_OPACITY = 0.2;

// Updated MemoizedRow with improved equality check
const MemoizedRow = memo(
  (props: GridRowProps) => <GridRow {...props} />,
  (prevProps, nextProps) => {
    // First check if the row IDs are different
    if (prevProps.row.id !== nextProps.row.id) return false;

    // Compare the previous and current row data
    const prevRow = prevProps.row;
    const nextRow = nextProps.row;

    // Get all keys from both rows to ensure we check all properties
    const prevKeys = Object.keys(prevRow);
    const nextKeys = Object.keys(nextRow);
    // Combine keys and make them unique manually without using Set
    const allKeys = [...prevKeys];

    // Add any keys from nextKeys that aren't already in allKeys
    for (const key of nextKeys) {
      if (!allKeys.includes(key)) {
        allKeys.push(key);
      }
    }

    // Check each property for changes
    for (const key of allKeys) {
      // Skip the id since we already checked it
      if (key === 'id') continue;

      // Check if the values are different
      if (prevRow[key] !== nextRow[key]) {
        return false; // Return false to trigger a re-render when any property changes
      }
    }

    // Also check if the selected state has changed
    if (prevProps.selected !== nextProps.selected) {
      return false;
    }

    // If we've made it here, the rows are considered equal
    return true;
  }
);

// Enhanced styled DataGrid with responsive features
const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  '& .MuiDataGrid-sortIcon': {
    opacity: 1,
    color: 'white'
  },
  '& .MuiDataGrid-filterIcon': {
    opacity: 1,
    color: 'white'
  },
  '& .MuiDataGrid-menuIconButton': {
    opacity: 1,
    color: 'white'
  },
  // Add responsive styles for the main grid container
  '& .MuiDataGrid-main': {
    // Enable horizontal scrolling
    overflow: 'auto'
  },
  // Ensure column headers stay visible during horizontal scroll
  '& .MuiDataGrid-columnHeaders': {
    position: 'sticky',
    top: 0,
    zIndex: 1
  },
  // Ensure proper virtual scrolling behavior
  '& .MuiDataGrid-virtualScroller': {
    overflowX: 'auto',
    overflowY: 'auto'
  },
  // Improve cell content display
  '& .MuiDataGrid-cell': {
    outline: 'none !important',
    whiteSpace: 'normal',
    lineHeight: 'normal',
    display: 'flex',
    alignItems: 'center',
    padding: '8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '& *': {
      '&:focus, &:focus-visible, &:active': {
        outline: 'none !important',
        boxShadow: 'none !important'
      }
    }
  },
  // Responsive text size adjustments
  [theme.breakpoints.down('md')]: {
    '& .MuiDataGrid-cell': {
      fontSize: '0.875rem'
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontSize: '0.875rem'
    }
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiDataGrid-cell': {
      fontSize: '0.75rem',
      padding: '4px'
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontSize: '0.75rem'
    }
  },
  // Rest of your existing styles
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[200],
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      '@media (hover: none)': {
        backgroundColor: 'transparent'
      }
    },
    '&.Mui-selected': {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity
      ),
      '&:hover': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
          theme.palette.action.selectedOpacity +
          theme.palette.action.hoverOpacity
        ),
        '@media (hover: none)': {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity
          )
        }
      },
      outline: 'none',
      boxShadow: 'none'
    }
  },
  [`& .${gridClasses.row}.odd`]: {
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      '@media (hover: none)': {
        backgroundColor: 'transparent'
      }
    },
    '&.Mui-selected': {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity
      ),
      outline: 'none',
      boxShadow: 'none'
    }
  },
  '& .MuiButtonBase-root': {
    '&:focus, &:focus-visible, &:active': {
      outline: 'none !important',
      boxShadow: 'none !important'
    }
  },
  WebkitTapHighlightColor: 'transparent'
}));

export type RecordValue =
  | string
  | number
  | undefined
  | object
  | React.ReactElement;

export type Record = {
  id: string; // Ensure that each record has a unique id
  [key: string]: RecordValue | RecordValue[];
};

interface DataGridProps {
  rows: Array<Record>;
  columns: any;
  loading?: boolean;
  checkboxSelection?: boolean;
  disableRowSelectionOnClick?: boolean;
  height?: string;
  selectedRows?: any;
  setSelectedRows?: any;
  isRowSelectable?: (params: any) => boolean;
  autoHeight?: boolean;
  sx?: any;
}

const DataGridComponent = ({
  rows,
  columns,
  loading,
  checkboxSelection,
  disableRowSelectionOnClick,
  height = '50vh',
  selectedRows,
  setSelectedRows,
  isRowSelectable,
  autoHeight = false,
  sx = {}
}: DataGridProps) => {
  const dispatch = useDispatch();

  const reduxFilterModel = useSelector(
    (state: RootState) => state.pagination.filterModel
  );

  // Initialize local state from Redux
  const [filterModel, setFilterModelState] = useState<GridFilterModel>(
    reduxFilterModel || { items: [] }
  );

  const [sortModel, setSortModelState] = useState<GridSortModel>(
    useSelector((state: RootState) => state.pagination.sortModel)
  );

  const [paginationModel, setPaginationModel] = useState({
    pageSize: useSelector((state: RootState) => state.pagination.rowsPerPage),
    page: useSelector((state: RootState) => state.pagination.pagenumber)
  });

  useEffect(() => {
    dispatch(clearSortModel());
  }, [])
  console.log(sortModel, "sortModel")
  // State to track viewport width for responsive adjustments
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

  // Keep a reference to the current rows for comparison
  const rowsRef = useRef(rows);

  // Effect to handle window resize
  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Update the rows reference when rows prop changes
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilterModelState(newFilterModel);
    dispatch(setFilterModel(newFilterModel));
  };

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModelState(newSortModel);
    dispatch(setSortModel(newSortModel));
  };

  const handlePaginationModelChange = (
    newPaginationModel: GridPaginationModel
  ) => {
    setPaginationModel(newPaginationModel);
    dispatch(setPageNumber(newPaginationModel.page));
    dispatch(setRowsPerPage(newPaginationModel.pageSize));
  };

  const getRowId = useCallback((row: Record) => row.id, []);

  const getRowClassName = useCallback(
    (params: any) =>
      params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd',
    []
  );

  // Define responsive columnBuffer based on viewport width
  const getColumnBuffer = () => {
    if (viewportWidth < 600) return 2; // Mobile
    if (viewportWidth < 960) return 4; // Tablet
    return 8; // Desktop
  };

  // Update local state when Redux state changes
  useEffect(() => {
    if (reduxFilterModel) {
      setFilterModelState(reduxFilterModel);
    }
  }, [reduxFilterModel]);

  return (
    <Box
      sx={{
        height: autoHeight ? 'auto' : height,
        width: '100%',
        border: 'none',
        borderRadius: '6px',
        '& .super-app-theme--header': {
          backgroundColor: '#102633',
          border: 'none',
          color: 'white',
          fontSize: viewportWidth < 768 ? '0.85rem' : '1vw',
          fontWeight: 'bold'
        },
        // Add a container for horizontal scrolling if needed
        overflow: 'hidden',
        ...sx
      }}
    >
      <StyledDataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        checkboxSelection={checkboxSelection}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[10]}
        getRowClassName={getRowClassName}
        onRowSelectionModelChange={newSelection => {
          if (setSelectedRows) {
            setSelectedRows(newSelection);
          }
        }}
        rowSelectionModel={selectedRows || []}
        isRowSelectable={isRowSelectable}
        getRowId={getRowId}
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton'
          }
        }}
        slots={{
          row: MemoizedRow
        }}
        rowBufferPx={40}
        columnBufferPx={getColumnBuffer()}
        autoHeight={autoHeight}
        // Added key props to ensure the grid re-renders properly when important props change
        key={`datagrid-${rows?.length || '0'}-${JSON.stringify(
          rows
        )}-${viewportWidth}`}
      />
    </Box>
  );
};

export default memo(DataGridComponent);
